import { useEffect, useState, useCallback } from "react";
import { Smartphone, Share, Plus, X, Sparkles } from "lucide-react";

/**
 * "Add to Home Screen" — a compact, pink pill CTA that lets attendees pin the
 * Business Showers site to their phone home screen like a native app.
 *
 * Behavior:
 *   • Auto-hides if the app is already running as a PWA (standalone display mode)
 *   • On Android/Chrome / Edge: captures the `beforeinstallprompt` event
 *     (set up in `/app/frontend/src/index.js`) and calls `prompt()` when the
 *     user taps the CTA
 *   • On iOS Safari (no install API): opens a bottom sheet with animated
 *     instructions pointing to the Share → "Add to Home Screen" flow
 *   • Dismissible for 7 days via localStorage (`bs_a2hs_dismissed_at`)
 *
 * Props:
 *   className?: string        — extra classes for the wrapping button
 *   variant?: "pill"|"card"   — visual density (default: pill)
 */
export default function AddToHomeScreen({ className = "", variant = "pill" }) {
  const [installable, setInstallable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [installed, setInstalled] = useState(false);

  const isStandalone = useCallback(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      // iOS Safari (older) sets window.navigator.standalone
      window.navigator?.standalone === true ||
      localStorage.getItem("bs_pwa_installed") === "1"
    );
  }, []);

  const isIOS = useCallback(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iOSNative = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // iPadOS 13+ reports as MacIntel but has multi-touch
    const iPadOS = /Mac/.test(ua) && navigator.maxTouchPoints > 1;
    return iOSNative || iPadOS;
  }, []);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }

    // Check if dismissed within last 7 days
    try {
      const d = parseInt(localStorage.getItem("bs_a2hs_dismissed_at") || "0", 10);
      if (d && Date.now() - d < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    } catch { /* noop */ }

    // Check if a beforeinstallprompt was already captured in index.js
    if (typeof window !== "undefined" && window.__bsInstallPrompt) setInstallable(true);

    const onAvail = () => setInstallable(true);
    const onDone = () => setInstalled(true);
    window.addEventListener("bs-install-available", onAvail);
    window.addEventListener("bs-install-done", onDone);
    return () => {
      window.removeEventListener("bs-install-available", onAvail);
      window.removeEventListener("bs-install-done", onDone);
    };
  }, [isStandalone]);

  const handleClick = async () => {
    if (installable && window.__bsInstallPrompt) {
      try {
        window.__bsInstallPrompt.prompt();
        const choice = await window.__bsInstallPrompt.userChoice;
        if (choice?.outcome === "accepted") setInstalled(true);
        window.__bsInstallPrompt = null;
        setInstallable(false);
      } catch { /* noop */ }
      return;
    }
    // iOS or unsupported → show instructions
    setShowIosSheet(true);
  };

  const dismiss = () => {
    try { localStorage.setItem("bs_a2hs_dismissed_at", String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
  };

  if (installed || dismissed) return null;

  const isPill = variant === "pill";

  return (
    <>
      <div className={`relative inline-flex items-center ${className}`} data-testid="a2hs-wrapper">
        <button
          type="button"
          onClick={handleClick}
          data-testid="a2hs-cta"
          className={
            isPill
              ? "inline-flex items-center gap-2 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white pl-4 pr-5 h-11 shadow-lg shadow-fuchsia-600/30 font-[Outfit] font-bold text-sm transition-all hover:-translate-y-0.5"
              : "w-full inline-flex items-start gap-3 rounded-2xl border-2 border-fuchsia-300 bg-gradient-to-br from-fuchsia-50 to-white px-4 py-3 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all text-left"
          }
        >
          {isPill ? (
            <>
              <Smartphone size={16} /> Add to Home Screen
            </>
          ) : (
            <>
              <span className="inline-flex h-10 w-10 rounded-xl bg-fuchsia-600 text-white items-center justify-center shrink-0">
                <Smartphone size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950 flex items-center gap-1.5">
                  Install this app on your phone
                  <Sparkles size={12} className="text-fuchsia-600" />
                </div>
                <div className="text-[11px] font-[Outfit] text-emerald-950/60 mt-0.5">
                  One tap. No download. Perfect for the event weekend.
                </div>
              </div>
            </>
          )}
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="a2hs-dismiss"
          aria-label="Dismiss install prompt"
          className={
            isPill
              ? "ml-1 h-11 w-8 inline-flex items-center justify-center text-fuchsia-600/60 hover:text-fuchsia-800"
              : "absolute top-2 right-2 h-7 w-7 rounded-full bg-white/80 hover:bg-white text-emerald-950/50 hover:text-emerald-950 inline-flex items-center justify-center border border-emerald-950/10"
          }
        >
          <X size={14} />
        </button>
      </div>

      {showIosSheet && (
        <div
          data-testid="a2hs-ios-sheet"
          className="fixed inset-0 z-[70] bg-emerald-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center animate-in fade-in duration-200 p-0 sm:p-6"
          onClick={() => setShowIosSheet(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl animate-in slide-in-from-bottom sm:zoom-in duration-300"
          >
            <div className="px-5 pt-4 pb-2 flex items-start gap-3">
              <span className="inline-flex h-10 w-10 rounded-xl bg-fuchsia-100 text-fuchsia-700 items-center justify-center shrink-0">
                <Smartphone size={18} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-700">
                  Install on iPhone
                </div>
                <div className="font-[Cormorant_Garamond] text-2xl leading-tight text-emerald-950 mt-0.5">
                  Add Business Showers to your Home Screen
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowIosSheet(false)}
                data-testid="a2hs-ios-close"
                aria-label="Close instructions"
                className="rounded-full h-9 w-9 inline-flex items-center justify-center bg-emerald-950/5 hover:bg-emerald-950/10 text-emerald-950 shrink-0"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 pb-5 pt-2">
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-8 w-8 rounded-full bg-emerald-950 text-white flex items-center justify-center font-[Outfit] font-bold text-sm">1</span>
                  <div className="text-sm font-[Outfit] text-emerald-950">
                    Tap the <span className="inline-flex items-center gap-1 rounded-md bg-fuchsia-50 border border-fuchsia-200 px-1.5 py-0.5 mx-0.5 align-middle text-fuchsia-800 font-semibold text-xs"><Share size={12} /> Share</span> icon at the bottom of Safari.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-8 w-8 rounded-full bg-emerald-950 text-white flex items-center justify-center font-[Outfit] font-bold text-sm">2</span>
                  <div className="text-sm font-[Outfit] text-emerald-950">
                    Scroll down and tap <span className="inline-flex items-center gap-1 rounded-md bg-fuchsia-50 border border-fuchsia-200 px-1.5 py-0.5 mx-0.5 align-middle text-fuchsia-800 font-semibold text-xs"><Plus size={12} /> Add to Home Screen</span>.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-8 w-8 rounded-full bg-fuchsia-600 text-white flex items-center justify-center font-[Outfit] font-bold text-sm">3</span>
                  <div className="text-sm font-[Outfit] text-emerald-950">
                    Tap <span className="font-bold">Add</span> in the top-right. Business Showers now lives on your home screen — one tap during the event weekend.
                  </div>
                </li>
              </ol>

              <div className="mt-5 rounded-2xl bg-fuchsia-50 border border-fuchsia-200 p-3 flex items-start gap-2">
                <Sparkles size={14} className="text-fuchsia-700 shrink-0 mt-0.5" />
                <div className="text-xs font-[Outfit] text-emerald-950/80">
                  <strong className="text-fuchsia-800">Not on Safari?</strong> Open <span className="font-mono text-[11px] bg-white px-1.5 py-0.5 rounded border border-fuchsia-200">pitch.businessshowers.org</span> in Safari first — Chrome on iPhone can&apos;t install web apps.
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowIosSheet(false)}
                data-testid="a2hs-ios-got-it"
                className="mt-4 w-full h-12 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-[Outfit] font-bold text-sm shadow-lg shadow-fuchsia-600/30"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
